<template>
  <div>
    <div class="drawer-title get-height py-2 px-3">
      <div>
        <v-layout>
          <v-flex>
            <span class="font-weight-semi-bold text--ellipsis">{{ $t('Views.AccessProfilesPage.selectEmployees') }}</span>
          </v-flex>
        </v-layout>
      </div>

      <div>
        <v-btn
          :disabled="!employeeList.length"
          flat
          icon
          @click="addEmployeeToProfile"
        >
          <v-icon>group_add</v-icon>
        </v-btn>

        <v-btn flat icon class="mr-0" @click="closeDrawer">
          <v-icon>close</v-icon>
        </v-btn>
      </div>
    </div>

    <div class="employee-list">
      <div class="employee-list__item employee-list__search get-height">
        <v-checkbox @change="selectAllEmployees" :disabled="!selectAllIsActive" />
        <v-text-field
          flat
          :label="`${$t(`Common.searchBy`)} ${$t(`Common.employee`)}`"
          prepend-inner-icon="search"
          solo
          color="accent"
          clearable
          hide-details
          v-model="search"
          background-color="#ffffff"
          @input="searchInput"
        />
      </div>

      <div
        class="content--overflow"
        :style="`height: ${contentHeight}px;`"
        @scroll="loadMore"
      >
        <!-- success -->
        <div v-if="employees.length"  class="employee-list__body">
          <div
            v-for="(employee, index) in employees"
            :key="index"
            :id="`employee-list__item--${employee.id}`"
            class="employee-list__item"
          >
            <v-checkbox v-model="employeeList" :value="employee.id" />
            <span class="employee-list__item--name">{{ employee.name | ellipsis(32) }}</span>
            <v-layout>
              <v-flex xs6 class="employee-list__item--phone">
                <v-icon>smartphone</v-icon>&nbsp;
                <router-link
                  v-if="!employee.phone"
                  :to="{ name: 'Employee', params: { type: 'editar', id: employee.id } }"
                  title=""
                  class="text--link accent--text"
                >
                  {{ $t('Common.uninformed') }}
                </router-link>
                <span v-else>{{ employee.phone | phoneWithDDD }}</span>
              </v-flex>
              <v-flex xs6 class="employee-list__item--email">
                <v-icon>email</v-icon>&nbsp;
                <router-link
                  v-if="!employee.email"
                  :to="{ name: 'Employee', params: { type: 'editar', id: employee.id } }"
                  title=""
                  class="text--link accent--text"
                >
                  {{ $t('Common.uninformed') }}
                </router-link>
                <span v-else>{{ employee.email | ellipsis(14) }}</span>
              </v-flex>
            </v-layout>
          </div>
        </div>

        <!-- no results -->
        <v-layout
          v-if="!employees.length && !isLoading && !isFailed"
          align-center
          justify-center
          row
          fill-height
        >
          <v-flex class="text-xs-center">
            <span>{{ $t('Views.EmployeesPage.noneEmployeeFound') }}</span>
          </v-flex>
        </v-layout>

        <!-- error -->
        <v-layout v-if="isFailed" align-center justify-center row fill-height>
          <v-flex class="text-xs-center">
            <span>
              {{ $t('Common.genericErrorMsg') }}<br>
              <a
                href="#"
                :title="$t('Common.tryAgain')"
                class="text--link accent--text"
                @click.prevent="getEmployeesList"
              >
                {{ $t('Common.tryAgain') }}
              </a>
            </span>
          </v-flex>
        </v-layout>

        <!-- loading data -->
        <transition name="fade">
          <div v-if="isLoading" class="text-xs-center py-4" >
            <v-progress-circular indeterminate color="accent" />
          </div>
        </transition>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import debounce from 'lodash.debounce'
import { calcContentHeight } from 'utils/functions'
import { STATUS_IS_LOADING, STATUS_SUCCESS, STATUS_FAILED } from 'utils/constants'

export default {
  name: 'EmployeeList',
  computed: {
    ...mapState({
      company: state => state.company.selectedCompany
    }),

    profile () {
      return 'PROFILE_NAME'
    },

    isLoading () {
      return this.currentStatus === STATUS_IS_LOADING
    },

    isSuccess () {
      return this.currentStatus === STATUS_SUCCESS
    },

    isFailed () {
      return this.currentStatus === STATUS_FAILED
    }
  },
  data () {
    return {
      currentStatus: null,
      selectAll: null,
      search: null,
      employees: [],
      employeeList: [],
      selectAllIsActive: true,
      contentHeight: 'auto',
      page: 1,
      isLastPage: false,
      limit: 25
    }
  },
  methods: {
    ...mapActions({
      successAlert: 'snackbarAlert/success',
      errorAlert: 'snackbarAlert/error'
    }),

    calcContentHeight () {
      this.contentHeight = calcContentHeight()
    },

    closeDrawer () {
      this.$emit('closeDrawer')
    },

    selectAllEmployees (val) {
      if (val) {
        this.employeeList = []
        this.employees.forEach(employee => {
          this.employeeList.push(employee.id)
        })
        return
      }

      this.employeeList = []
    },

    toggleSelectedEmployee (items) {
      const activeClass = 'employee-list__item--active'
      const activeItems = document.querySelectorAll(`.${activeClass}`)
      activeItems.forEach(item => {
        item.classList.remove(activeClass)
      })

      items.forEach(employee => {
        const elem = document.querySelector(`#employee-list__item--${employee}`)
        if (elem) {
          elem.classList.add(activeClass)
        }
      })
    },

    async getEmployeesList () {
      if (this.isLastPage) return
      this.currentStatus = STATUS_IS_LOADING
      this.selectAllIsActive = true

      try {
        const company = this.company ? this.company.id : null
        const search = this.search ? this.search : null
        const { data: res } = await this.$apiService.getEmployees(
          { limit: this.limit },
          { page: this.page },
          { company },
          { search }
        )

        if (!res.data.length) {
          this.isLastPage = true
        } else {
          this.employees = this.employees.concat(res.data)
        }

        this.currentStatus = STATUS_SUCCESS
        if (search) {
          this.selectAllIsActive = false
        }
      } catch (err) {
        console.log('Error', err)
        this.currentStatus = STATUS_FAILED
      }

      window.setTimeout(() => {
        this.toggleSelectedEmployee(this.employeeList)
      }, 200)
    },

    loadMore () {
      if (this.isLoading) return
      const wrapper = event.target
      if (wrapper.offsetHeight + wrapper.scrollTop === wrapper.scrollHeight) {
        this.page += 1
        this.getEmployeesList()
      }
    },

    searchInput: debounce(function () {
      this.employees = []
      this.page = 1
      this.getEmployeesList()
    }, 1000),

    addEmployeeToProfile () {
      // success
      this.successAlert(`
        <strong>${this.employeeList.length} ${this.$t('Components.AccessProfilesDrawer.employees')}</strong>
        ${this.$t('Components.AccessProfilesDrawer.employeesAddSuccess')} "${this.profile}"
      `)
      this.closeDrawer()
    }
  },
  watch: {
    employeeList (items) {
      this.toggleSelectedEmployee(items)
    },

    company () {
      this.closeDrawer()
    }
  },
  mounted () {
    this.calcContentHeight()
    this.getEmployeesList()
    window.addEventListener('resize', this.calcContentHeight)
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.calcContentHeight)
  }
}
</script>

<style lang="scss">
.employee-list {

  .employee-list__item {
    border-bottom: 1px solid $gray-4;
    height: 64px;
    padding-left: 56px;
    padding-top: 10px;
    position: relative;
    transition: background-color .26s;

    &.employee-list__item--active {
      background-color: #fffadd;
    }

    .v-input--checkbox {
      left: 15px;
      position: absolute;
      top: 0px;

      .v-input__slot {
        margin-bottom: 0;
      }
    }

    .employee-list__item--name,
    .employee-list__item--phone,
    .employee-list__item--email {

      .v-icon {
        font-size: 16px;
      }
    }

    .employee-list__item--name {
      display: block;
      font-weight: 600;
      margin-bottom: 2px;
    }

    .employee-list__item--phone,
    .employee-list__item--email {
      color: $gray-6;
    }
  }

  .employee-list__search {
    background-color: $gray-2;
    padding: 14px 16px 0 56px;

    .v-text-field {
      height: 36px;

      .v-input__control {
        min-height: 36px;

        .v-label,
        input {
          font-size: 12px;
        }
      }
    }
  }
}
</style>
